import React, { useState, useCallback, useEffect, useRef} from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken.js";
import { db } from "../../firebase"; // adjust path if needed
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth.js"; // or however you get the user/kid info

/**
 * MathAddingPractice – interactive addition drills + printable worksheets
 * Now with a victory sound effect every time the student gets an answer right.
 */

/* ────────── CONFIG ────────── */
// Place an MP3/OGG file at public/sounds/correct.mp3 (or change the path).
const SOUND_SOURCES = [
  "/sounds/correct.mp3",   // broad support (Chrome, Edge, Safari)
  "/sounds/correct.ogg",   // good for Firefox (optional – add if you convert)
  "/sounds/correct.wav",   // fallback
];

const DIGIT_OPTIONS = [
  { label: "Single-digit (x + x)", value: 1 },
  { label: "Double-digit (xx + xx)", value: 2 },
  { label: "Triple-digit (xxx + xxx)", value: 3 },
];

const SHEET_SIZES = [10, 25, 50];

/* Utility helpers */
const randIntInclusive = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const generateNumber = (digits) => {
  if (digits === 1) return randIntInclusive(1, 9);
  const min = 10 ** (digits - 1);
  const max = 10 ** digits - 1;
  return randIntInclusive(min, max);
};

const generateProblem = (digits) => ({
  a: generateNumber(digits),
  b: generateNumber(digits),
});

const generateProblemSet = (count, digits) =>
  Array.from({ length: count }, () => generateProblem(digits));

/* ────────── PRACTICE-VIEW PROBLEM RENDERER ────────── */
const VerticalProblem = React.memo(({ a, b, showAnswer = false }) => {
  const aStr = String(a);
  const bStr = String(b);
  const len = Math.max(aStr.length, bStr.length);

  // Leading space aligns digits beneath the plus column
  const line1 = " " + aStr.padStart(len, " ");
  const line2 = "+" + bStr.padStart(len, " ");
  const separator = "-".repeat(len + 1);
  const answer = " " + String(a + b).padStart(len, " ");

  return (
    <pre className="vertical-problem">
      {line1}
      {"\n"}
      {line2}
      {"\n"}
      {separator}
      {showAnswer && ("\n" + answer)}
    </pre>
  );
});

const TOOL_TYPE = "MathAddingPractice";

async function logAdditionPractice({ user, digitCount, numQuestions, correct, attempts, startTime, endTime }) {
  if (!user?.uid) {
    console.log("No user, not logging practice.");
    return;
  }
  try {
    await setDoc(
  doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
  {
    toolType: TOOL_TYPE,
    topic: `${digitCount}-digit addition`,
    numQuestions,
    correct,
    attempts,
    timestamp: serverTimestamp(),
    startedAt: startTime,
    finishedAt: endTime,
    durationSec: Math.round((endTime - startTime) / 1000),
  },
  { merge: true }
);
  } catch (err) {
    console.error("Failed to write practice record:", err);
  }
}

/* ────────── WORKSHEET COLUMN RENDERER ────────── */
function WorksheetColumnProblem({ a, b }) {
  const aStr = String(a);
  const bStr = String(b);
  const maxLen = Math.max(aStr.length, bStr.length);
  return (
    <div className="math-col">
      <div className="math-top">{aStr.padStart(maxLen, "\u00a0")}</div>
      <div className="math-bottom">+{bStr.padStart(maxLen, "\u00a0")}</div>
      <div className="math-underline" />
      <div className="math-answer-space">&nbsp;</div>
    </div>
  );
}

/* ────────── MAIN COMPONENT ────────── */
export default function MathAddingPractice({ onAwardTokens } = {}) {
  /* State */
  const [digitCount, setDigitCount] = useState(1);
  const [current, setCurrent] = useState(() => generateProblem(1));
  const [userAnswer, setUserAnswer] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [sheetProblems, setSheetProblems] = useState(null);
  const correctSoundRef = useRef(null);
  const [feedback, setFeedback] = useState(null); // feedback string (wrong answer)
  const [waitingForNext, setWaitingForNext] = useState(false);
  const tokensAwarded = useRef(0);   // how many we’ve given in this session
  const { user } = useAuth(); // gets current kid user
  const [sessionStart, setSessionStart] = useState(Date.now());


useEffect(() => {
  if (correct >= (tokensAwarded.current + 1) * 10) {
    awardToken();
    tokensAwarded.current += 1;
    if (onAwardTokens) onAwardTokens(1);
  }
}, [correct, onAwardTokens]);

useEffect(() => {
  if (attempts > 0 && attempts % 10 === 0) {
    const endTime = Date.now();
    logAdditionPractice({
      user,
      digitCount,
      numQuestions: attempts,
      correct,
      attempts,
      startTime: sessionStart,
      endTime,
    });
    setSessionStart(endTime);
    setAttempts(0);
    setCorrect(0);
  }
  // eslint-disable-next-line
}, [attempts]);


useEffect(() => {
  const audio = new Audio();
  // Pick the first format the browser reports it can play
  for (const src of SOUND_SOURCES) {
    const type = src.endsWith(".mp3")
      ? "audio/mpeg"
      : src.endsWith(".ogg")
      ? "audio/ogg"
      : "audio/wav";
    if (audio.canPlayType(type)) {
      audio.src = src;
      break;
    }
  }
  correctSoundRef.current = audio;
}, []);

const logAndResetSession = useCallback(() => {
  if (attempts > 0) {
    const endTime = Date.now();
    logAdditionPractice({
      user,
      digitCount,
      numQuestions: attempts,
      correct,
      attempts,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setAttempts(0);
  setCorrect(0);
}, [attempts, correct, digitCount, sessionStart, user]);

useEffect(() => {

  if (attempts > 0) {
    const endTime = Date.now();
    logAdditionPractice({
      user,
      digitCount,
      numQuestions: attempts,
      correct,
      attempts,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setAttempts(0);
  setCorrect(0);
  setCurrent(generateProblem(digitCount));
  setUserAnswer("");
  // eslint-disable-next-line
}, [digitCount]);


useEffect(() => {
  return () => {
    if (attempts > 0) {
      const endTime = Date.now();
      logAdditionPractice({
        user,
        digitCount,
        numQuestions: attempts,
        correct,
        attempts,
        startTime: sessionStart,
        endTime,
      });
    }
  };
  // eslint-disable-next-line
}, []);

  /* Handlers */
const handleAnswerSubmit = (e) => {
  e.preventDefault();
  if (userAnswer.trim() === "" || waitingForNext) return;
  const isCorrect = Number(userAnswer) === current.a + current.b;

  // Compute new scores:
  const nextAttempts = attempts + 1;
  const nextCorrect = isCorrect ? correct + 1 : correct;

  setAttempts(nextAttempts);
  setCorrect(nextCorrect);

  // Always update Firestore after every attempt!
  const endTime = Date.now();
  logAdditionPractice({
    user,
    digitCount,
    numQuestions: nextAttempts,
    correct: nextCorrect,
    attempts: nextAttempts,
    startTime: sessionStart,
    endTime,
  });

  if (isCorrect) {
    if (correctSoundRef.current) {
      correctSoundRef.current.currentTime = 0;
      correctSoundRef.current.play();
    }
    setUserAnswer("");
    setFeedback(null);
    setTimeout(() => {
      setCurrent(generateProblem(digitCount));
    }, 0);
  } else {
    setFeedback(`❌ The correct answer is: ${current.a + current.b}`);
    setWaitingForNext(true);
  }
};



  const startSheetMode = (count) => {
    const problems = generateProblemSet(count, digitCount);
    setSheetProblems(problems);
    setTimeout(() => window.print(), 100);
  };
  const leaveSheetMode = () => setSheetProblems(null);

  /* ────────── RENDER ────────── */
  if (sheetProblems) {
    return (
      <div className="worksheet">
        <h2 className="no-print">Addition Worksheet – {digitCount}-digit</h2>
        <button className="no-print" onClick={leaveSheetMode}>
          Back to Practice
        </button>
        <div className="sheet-grid">
          {sheetProblems.map((p, idx) => (
            <WorksheetColumnProblem key={idx} {...p} />
          ))}
        </div>
      </div>
    );
  }

return (
  <div className="practice-container">
    <h2>Addition Practice</h2>

    <fieldset>
      <legend>Choose number size:</legend>
      {DIGIT_OPTIONS.map(({ label, value }) => (
        <label key={value} style={{ marginRight: "1rem" }}>
          <input
            type="radio"
            checked={digitCount === value}
            onChange={() => setDigitCount(value)}
          />
          {" "}
          {label}
        </label>
      ))}
    </fieldset>

    <VerticalProblem a={current.a} b={current.b} />

    <form onSubmit={handleAnswerSubmit} className="answer-form">
      <input
        type="number"
        value={userAnswer}
        onChange={(e) => setUserAnswer(e.target.value)}
        autoFocus
        disabled={waitingForNext}
      />
      <button
        type="submit"
        style={{ marginLeft: "0.5rem" }}
        disabled={waitingForNext}
      >
        Submit
      </button>
    </form>

    {feedback && (
      <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
        {feedback}
      </div>
    )}

    {waitingForNext && (
      <button
        className="math-pill-btn"
        style={{ marginTop: "0.7em" }}
        onClick={() => {
          setCurrent(generateProblem(digitCount));
          setUserAnswer("");
          setFeedback(null);
          setWaitingForNext(false);
        }}
      >
        Next
      </button>
    )}

    <p>
      Score: {correct} / {attempts}
    </p>
    
    <div className="worksheet-buttons">
      <strong>Printable worksheets:</strong>
      {SHEET_SIZES.map((cnt) => (
        <button
          key={cnt}
          onClick={() => startSheetMode(cnt)}
          style={{ marginLeft: "0.5rem" }}
        >
          {cnt} Questions
        </button>
      ))}
    </div>
  </div>
);
}
